import { useCallback } from 'react'

import { setDataByPage, useQueryClient } from '@fv/client-core'
import type { FullShipment, ListShipment } from '@fv/client-types'

import { quoteRequestKeys } from '@/features/quote-request/useQuoteRequest'

type UseUpdateCachedLoadProps = {
  loadId?: string | null
  quoteRequestId?: string | null
  keysToSkip?: string[]
  updatedLoad?: ListShipment
}

function mergeShipment<T extends ListShipment | FullShipment>(
  prev: T,
  patch: ListShipment,
): T {
  if (prev.loadId !== patch.loadId) return prev
  return { ...prev, ...patch }
}

// Patches list-shaped API responses into cached queries.
export function useUpdateCachedLoad() {
  const queryClient = useQueryClient()

  const updateCachedLoad = useCallback(
    ({
      loadId = null,
      quoteRequestId = null,
      keysToSkip = [],
      updatedLoad,
    }: UseUpdateCachedLoadProps) => {
      if (!updatedLoad) return

      loadId = loadId ?? updatedLoad.loadId
      quoteRequestId = quoteRequestId ?? updatedLoad.quoteRequestId

      if (!loadId || !quoteRequestId) {
        return console.error(
          '`loadId` and `quoteRequestId` are required to update a cached load.',
        )
      }

      if (!keysToSkip.includes('shipments')) {
        const queryCache = queryClient.getQueryCache()
        const shipmentListQueries = queryCache.findAll({
          queryKey: ['shipments'],
        })
        const idleListQueries = shipmentListQueries.filter(
          q => q.state.fetchStatus !== 'fetching' && !q.state.isInvalidated,
        )

        idleListQueries.forEach(q => {
          queryClient.setQueryData(
            q.queryKey,
            setDataByPage<ListShipment>(data =>
              data.map(prev => mergeShipment(prev, updatedLoad)),
            ),
          )
        })
      }

      if (
        !keysToSkip.includes('load') &&
        queryClient.getQueryData(['load', loadId])
      ) {
        queryClient.setQueryData<FullShipment>(['load', loadId], prev =>
          prev ? mergeShipment(prev, updatedLoad) : prev,
        )
      }

      if (!keysToSkip.includes('quote-request')) {
        queryClient.setQueriesData<FullShipment[]>(
          { queryKey: quoteRequestKeys.request(quoteRequestId) },
          prev => {
            if (!prev?.some(load => load.loadId === loadId)) return prev
            return prev.map(prevLoad => mergeShipment(prevLoad, updatedLoad))
          },
        )
      }
    },
    [queryClient],
  )

  return updateCachedLoad
}
